import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import {
  CurrencyISOCode,
  FormattedPriceCalculationResponse
} from '@atlassiansox/bxp-use-pricing';
import styled from '@emotion/styled';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDesktopMediaQuery } from '../../hooks';
import { deviceMediaQueries, TRACKING_EVENT_NAMES } from '../../utils';
import { UnitCount } from '../unit-count';
import { PricingExampleContainerProps } from './pricing-example-container';
import { BoxShadowContainer, GridContainer } from './pricing-example-default';
import { PricingExample } from './pricing-example-states';
import { PricingExampleButton } from './pricing-example-button';

type PricingExampleNoSelectorProps = Omit<
  PricingExampleContainerProps,
  'hideBillingSelector'
> & {
  priceCalc: FormattedPriceCalculationResponse;
  currencyISOCode: CurrencyISOCode;
};

const PricingExampleNoSelector = ({
  unitTrackingTerm,
  pricingExampleUnitCounts,
  priceCalc,
  showFirstFlatRateTier,
  currencyISOCode
}: PricingExampleNoSelectorProps) => {
  const [showExample, setShowExample] = useState(false);
  const { createAnalyticsEvent } = useAnalyticsEvents();
  const isDesktop = useDesktopMediaQuery();

  // send analytics event if pricing example has been shown
  useEffect(() => {
    const analyticsEvent = createAnalyticsEvent({
      name: !showExample
        ? TRACKING_EVENT_NAMES.showPricingExampleTriggered
        : TRACKING_EVENT_NAMES.hidePricingExampleTriggered
    });
    analyticsEvent.fire();
  }, [showExample]);
  return (
    <BoxShadowContainer>
      <NoSelectorGridContainer>
        <UnitCount unitTrackingTerm={unitTrackingTerm} />
        {isDesktop && (
          <ShowHideExample>
            <PricingExampleButton
              onClickHandler={() => {
                setShowExample(!showExample);
              }}
            >
              {showExample ? (
                <FormattedMessage
                  id="hideExampleLabel"
                  defaultMessage="Hide pricing example"
                  description="Label for hide button to close the example display."
                />
              ) : (
                <FormattedMessage
                  id="showExampleLabel"
                  defaultMessage="See pricing example"
                  description="Label for button to show pricing example."
                />
              )}
            </PricingExampleButton>
          </ShowHideExample>
        )}
        {!showExample && !isDesktop && (
          <ExampleLabel>
            <PricingExampleButton
              onClickHandler={() => {
                setShowExample(true);
              }}
            >
              <FormattedMessage
                id="showExampleLabel"
                defaultMessage="See pricing example"
                description="Label for button to show pricing example."
              />
            </PricingExampleButton>
          </ExampleLabel>
        )}

        {showExample && (
          <PricingExample
            priceCalc={priceCalc}
            unitCounts={pricingExampleUnitCounts}
            showFirstFlatRateTier={showFirstFlatRateTier}
            currencyISOCode={currencyISOCode}
          />
        )}

        {showExample && !isDesktop && (
          <ExampleLabel>
            <PricingExampleButton
              onClickHandler={() => {
                setShowExample(false);
              }}
            >
              <FormattedMessage
                id="hideExampleLabel"
                defaultMessage="Hide pricing example"
                description="Label for hide button to close the example display."
              />
            </PricingExampleButton>
          </ExampleLabel>
        )}
      </NoSelectorGridContainer>
    </BoxShadowContainer>
  );
};

const NoSelectorGridContainer = styled.div`
  ${GridContainer}
`;

const ShowHideExample = styled.div`
  ${deviceMediaQueries.desktop} {
    padding-top: 4px;
  }

  button {
    ${deviceMediaQueries.desktop} {
      line-height: 32px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
`;

const ExampleLabel = styled.div`
  button {
    margin: 0px;
    padding: 0px;
  }
`;
export { PricingExampleNoSelector };
